import { fetchAuthSession } from 'aws-amplify/auth'
import { env } from '../config/env'

export type ChimeJoinTokenRequest = {
  meetingId?: string
  externalMeetingId?: string
  attendeeDisplayName?: string
  enableVideo?: boolean
  /**
   * Ask the server to DEFER recording/transcription start until the callee's
   * recording consent resolves (set by the 1:1 caller's dial-time prewarm,
   * where the callee has not yet seen the consent notice). Only honored on the
   * meeting-creating mint; capture then starts server-side when the callee's
   * consent-carrying ACCEPT/CONSENT signal arrives.
   */
  deferRecordingCapture?: boolean
}

export type ChimeMediaPlacement = {
  AudioHostUrl: string
  AudioFallbackUrl?: string
  SignalingUrl?: string
  TurnControlUrl?: string
  ScreenDataUrl?: string
  ScreenSharingUrl?: string
  ScreenViewingUrl?: string
  EventIngestionUrl?: string
}

export type ChimeMeetingInfo = {
  MeetingId: string
  MediaRegion: string
  MediaPlacement: ChimeMediaPlacement
  ExternalMeetingId?: string
}

export type ChimeAttendeeInfo = {
  AttendeeId: string
  JoinToken: string
  ExternalUserId?: string
}

/** Thrown when the meeting is already at the 20-participant cap (HTTP 409). */
export class CallFullError extends Error {
  readonly code = 'CALL_FULL'
  constructor(message = 'This call is limited to 20 participants') {
    super(message)
    this.name = 'CallFullError'
  }
}

/** Thrown when the server says the call already ended (HTTP 410 CALL_ENDED). */
export class CallEndedError extends Error {
  readonly code = 'CALL_ENDED'
  constructor(message = 'This call has already ended') {
    super(message)
    this.name = 'CallEndedError'
  }
}

export type ChimeJoinTokenResponse = {
  meetingId: string
  attendeeId: string
  joinToken: string
  mediaRegion: string
  externalUserId?: string
  displayName?: string
  meeting: { Meeting: ChimeMeetingInfo }
  attendee: { Attendee: ChimeAttendeeInfo }
}

/**
 * Amazon Chime SDK Meetings client — requests join tokens from API Gateway Lambda.
 */
class ChimeService {
  async requestJoinToken(request: ChimeJoinTokenRequest): Promise<ChimeJoinTokenResponse> {
    if (!env.chimeApiUrl) {
      throw new Error(
        'VITE_CHIME_API_URL is not set — deploy AscendChimeApi stack via infrastructure/scripts/deploy-chat.sh',
      )
    }

    const session = await fetchAuthSession()
    // API Gateway Cognito user pool authorizers validate the ID token, not the access token.
    const token = session.tokens?.idToken?.toString()
    if (!token) throw new Error('Not authenticated')

    const base = env.chimeApiUrl.endsWith('/') ? env.chimeApiUrl : `${env.chimeApiUrl}/`
    let res: Response
    try {
      // Hard timeout: a join-token request that HANGS (flaky mobile network,
      // mid-call network handover) left the caller stuck on "Connecting…"
      // forever — the join awaited this fetch with no recovery path. A thrown
      // timeout instead flows into the join's error/retry handling.
      res = await fetch(`${base}meetings/join-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
        signal: AbortSignal.timeout(15_000),
      })
    } catch (err) {
      if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
        throw new Error('Voice call server timed out — check your connection and try again.')
      }
      const hint =
        err instanceof TypeError && err.message === 'Failed to fetch'
          ? ' Could not reach the voice call server — check your connection and try again.'
          : ''
      throw new Error(
        err instanceof Error ? `${err.message}${hint}` : 'Could not reach the voice call server',
      )
    }

    const body = (await res.json().catch(() => ({}))) as {
      message?: string
      error?: string
    } & Partial<ChimeJoinTokenResponse>

    if (!res.ok) {
      const detail = body.message ?? body.error
      if (res.status === 401 || res.status === 403) {
        throw new Error(detail ?? 'Session expired — sign out and sign in again, then retry the call.')
      }
      // 20-participant cap enforced server-side (ListAttendees before CreateAttendee).
      if (res.status === 409 || body.error === 'CALL_FULL') {
        throw new CallFullError(detail ?? 'This call is limited to 20 participants')
      }
      if (res.status === 410 || body.error === 'CALL_ENDED') {
        throw new CallEndedError('This call has already ended')
      }
      throw new Error(detail ?? `Voice call server error (${res.status})`)
    }

    const placement = body.meeting?.Meeting?.MediaPlacement
    if (
      !body.meetingId ||
      !body.attendeeId ||
      !body.joinToken ||
      !body.mediaRegion ||
      !placement?.AudioHostUrl ||
      !placement?.SignalingUrl ||
      !body.attendee?.Attendee?.JoinToken
    ) {
      const missing: string[] = []
      if (!placement?.AudioHostUrl) missing.push('audioHostUrl')
      if (!placement?.SignalingUrl) missing.push('signalingUrl')
      if (!body.meeting?.Meeting) missing.push('meeting')
      if (!body.attendee?.Attendee?.JoinToken) missing.push('joinToken')
      throw new Error(
        missing.length > 0
          ? `Chime API returned incomplete meeting credentials (${missing.join(', ')})`
          : 'Chime API returned incomplete meeting credentials',
      )
    }

    return body as ChimeJoinTokenResponse
  }
}

export const chimeService = new ChimeService()
